import type { Getter, Row } from "@tanstack/react-table";
import type { TFunction } from "i18next";
import type { ReactNode } from "react";
import moment from "moment";
import styled from "styled-components/macro";
import { StatusBadge } from "../../../../../components/Status/Status";
import type {
  ApprovalListDetailConfig,
  ApprovalWorkflow,
} from "../../../../../types/types.Workflow";
import {
  getHighlightSegments,
  SearchHighlight,
} from "../../../../../util/collectionSearchUtils";
import {
  approvalStatusColor,
  approvalStatusLabel,
  findActiveApprovalStage,
} from "../utils";
import { ApprovalsListAssigneeCell } from "./ApprovalsListAssigneeCell";
import type { ApprovalsListVariant } from "./approvalsList.types";

const TwoLineCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  min-width: 0;
`;

const PrimaryLine = styled.span`
  color: ${({ theme }) => theme.resolve("color_text_primary")};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SecondaryLine = styled.span`
  color: ${({ theme }) => theme.resolve("color_text_secondary")};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TruncateCell = styled.span`
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

function cellMatchesSearch(text: string, searchQuery: string): boolean {
  const query = searchQuery.trim();
  return !!query && text.toLowerCase().includes(query.toLowerCase());
}

/** Plain text: yellow cell + bold match (same as collection search). */
function HighlightedText({
  text,
  searchQuery,
}: {
  text: string;
  searchQuery: string;
}) {
  const query = searchQuery.trim();
  const highlighted = cellMatchesSearch(text, query);
  return (
    <SearchHighlight $active={highlighted}>
      {highlighted ? (
        <span>
          {getHighlightSegments(text, query).map((segment, index) =>
            segment.matched ? <b key={index}>{segment.text}</b> : segment.text
          )}
        </span>
      ) : (
        text
      )}
    </SearchHighlight>
  );
}

/** Complex cell: yellow background when display text matches. */
function HighlightedCell({
  searchText,
  searchQuery,
  children,
}: {
  searchText: string;
  searchQuery: string;
  children: ReactNode;
}) {
  return (
    <SearchHighlight $active={cellMatchesSearch(searchText, searchQuery)}>
      {children}
    </SearchHighlight>
  );
}

function formatDetailValue(
  value: string | string[] | null | undefined
): string {
  if (value == null || value === "") {
    return "--";
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return "--";
    }
    if (value.length === 1) {
      return value[0];
    }
    return `${value[0]} +${value.length - 1}`;
  }
  return String(value);
}

/** Full multi-value list for tooltip when cell shows "+N". */
function detailValueTooltip(
  value: string | string[] | null | undefined
): string | undefined {
  if (!Array.isArray(value) || value.length <= 1) {
    return undefined;
  }
  return value.map(String).join(", ");
}

function DetailValueCell({
  value,
  searchQuery,
}: {
  value: string | string[] | null | undefined;
  searchQuery: string;
}) {
  const display = formatDetailValue(value);
  return (
    <span title={detailValueTooltip(value)}>
      <HighlightedText text={display} searchQuery={searchQuery} />
    </span>
  );
}

function buildConfiguredDetailColumns(
  t: TFunction,
  listDetails: ApprovalListDetailConfig[],
  searchQuery: string
) {
  const columns: Array<Record<string, unknown>> = [];
  let index = 0;
  while (index < listDetails.length) {
    const detail = listDetails[index];
    if (detail.show_in_table === false) {
      index += 1;
      continue;
    }
    const next = listDetails[index + 1];
    const merge =
      detail.merge_with_next === true && next && next.show_in_table !== false;

    if (merge && next) {
      const primaryKey = detail.key;
      const secondaryKey = next.key;
      const heading =
        detail.heading && next.heading
          ? `${detail.heading} / ${next.heading}`
          : detail.heading || next.heading || primaryKey;
      columns.push({
        header: t(heading),
        id: `detail_${primaryKey}_${secondaryKey}`,
        enableSorting: false,
        cell: ({ row }: { row: Row<ApprovalWorkflow> }) => {
          const details = row.original.item_details ?? {};
          return (
            <TwoLineCell>
              <PrimaryLine>
                <DetailValueCell
                  value={details[primaryKey]}
                  searchQuery={searchQuery}
                />
              </PrimaryLine>
              <SecondaryLine>
                <DetailValueCell
                  value={details[secondaryKey]}
                  searchQuery={searchQuery}
                />
              </SecondaryLine>
            </TwoLineCell>
          );
        },
      });
      index += 2;
      continue;
    }

    const key = detail.key;
    columns.push({
      header: t(detail.heading || key),
      id: `detail_${key}`,
      enableSorting: false,
      cell: ({ row }: { row: Row<ApprovalWorkflow> }) => (
        <DetailValueCell
          value={row.original.item_details?.[key]}
          searchQuery={searchQuery}
        />
      ),
    });
    index += 1;
  }
  return columns;
}

type ColumnArgs = {
  t: TFunction;
  variant: ApprovalsListVariant;
  skuIdHeader: string;
  listDetails?: ApprovalListDetailConfig[];
  onAssigneeUpdated?: () => unknown;
  searchQuery?: string;
};

/**
 * Product vs dashboard Approvals list column order.
 */
export function buildApprovalsListColumns({
  t,
  variant,
  skuIdHeader,
  listDetails = [],
  onAssigneeUpdated,
  searchQuery = "",
}: ColumnArgs) {
  const configuredColumns = buildConfiguredDetailColumns(
    t,
    listDetails,
    searchQuery
  );

  const firstColumn =
    variant === "dashboard"
      ? {
          header: t("Product Name"),
          id: "product_name",
          enableSorting: false,
          cell: ({ row }: { row: Row<ApprovalWorkflow> }) => {
            const text =
              row.original.product_name || row.original.product_number || "--";
            return (
              <TruncateCell title={text === "--" ? undefined : text}>
                <HighlightedText text={text} searchQuery={searchQuery} />
              </TruncateCell>
            );
          },
        }
      : {
          header: t("ID"),
          accessorKey: "number",
          enableSorting: false,
          cell: ({ getValue }: { getValue: Getter<string> }) => (
            <HighlightedText
              text={getValue() || "--"}
              searchQuery={searchQuery}
            />
          ),
        };

  const skuIdColumn = {
    header: t(skuIdHeader),
    accessorKey: "id_value",
    enableSorting: false,
    cell: ({ getValue }: { getValue: Getter<string> }) => (
      <HighlightedText text={getValue() || "--"} searchQuery={searchQuery} />
    ),
  };

  const assigneeColumn = {
    header: t("Assignee"),
    id: "assignee",
    enableSorting: false,
    meta: {
      overflow: "visible" as const,
      cellPadding: "6px 12px",
      maxWidth: "none",
    },
    cell: ({ row }: { row: Row<ApprovalWorkflow> }) => {
      const stage = findActiveApprovalStage(
        row.original.stages ?? [],
        row.original.current_stage
      );
      const assigneeName = stage?.assignee?.name || "";
      return (
        <HighlightedCell searchText={assigneeName} searchQuery={searchQuery}>
          <ApprovalsListAssigneeCell
            approval={row.original}
            onAssigneeUpdated={onAssigneeUpdated}
          />
        </HighlightedCell>
      );
    },
  };

  const statusColumn = {
    header: t("Status"),
    accessorKey: "status",
    enableSorting: false,
    cell: ({ getValue }: { getValue: Getter<string> }) => {
      const status = getValue();
      const label = approvalStatusLabel(status, t);
      return (
        <HighlightedCell searchText={label} searchQuery={searchQuery}>
          <StatusBadge color={approvalStatusColor(status)} text={label} />
        </HighlightedCell>
      );
    },
  };

  const stageColumn = {
    header: t("Stage"),
    accessorKey: "current_stage",
    enableSorting: false,
    cell: ({
      getValue,
      row,
    }: {
      getValue: Getter<string>;
      row: { original: ApprovalWorkflow };
    }) => {
      const value = getValue();
      const match = findActiveApprovalStage(row.original.stages ?? [], value);
      const text = match?.display_name || value || "--";
      return <HighlightedText text={text} searchQuery={searchQuery} />;
    },
  };

  const requestedByColumn = {
    header: t("Requested By"),
    id: "requested_by",
    enableSorting: false,
    cell: ({ row }: { row: Row<ApprovalWorkflow> }) => (
      <TwoLineCell>
        <PrimaryLine>
          <HighlightedText
            text={row.original.requested_by_name || "--"}
            searchQuery={searchQuery}
          />
        </PrimaryLine>
        <SecondaryLine>
          {row.original.requested_at
            ? moment(row.original.requested_at).format("MMM DD, YYYY")
            : "--"}
        </SecondaryLine>
      </TwoLineCell>
    ),
  };

  const lastModifiedColumn = {
    header: t("Last Modified By"),
    id: "last_modified",
    enableSorting: false,
    cell: ({ row }: { row: Row<ApprovalWorkflow> }) => (
      <TwoLineCell>
        <PrimaryLine>
          <HighlightedText
            text={row.original.modified_by || "--"}
            searchQuery={searchQuery}
          />
        </PrimaryLine>
        <SecondaryLine>
          {row.original.modified_at
            ? moment(row.original.modified_at).format("MMM DD, YYYY")
            : "--"}
        </SecondaryLine>
      </TwoLineCell>
    ),
  };

  const approvalTypeColumn = {
    header: t("Approval Type"),
    accessorKey: "approval_type",
    enableSorting: false,
    cell: ({ getValue }: { getValue: Getter<string> }) => (
      <HighlightedText text={getValue() || "--"} searchQuery={searchQuery} />
    ),
  };

  if (variant === "dashboard") {
    // Product Name, SKU, configured attrs, Assignee, Status, Stage,
    // Requested By (name + date), Approval Type
    return [
      firstColumn,
      skuIdColumn,
      ...configuredColumns,
      assigneeColumn,
      statusColumn,
      stageColumn,
      requestedByColumn,
      approvalTypeColumn,
    ];
  }

  // ID, SKU ID, configured attrs, Assignee, Status, Stage,
  // Requested By (name + date), Last Modified By (name + date), Approval Type
  return [
    firstColumn,
    skuIdColumn,
    ...configuredColumns,
    assigneeColumn,
    statusColumn,
    stageColumn,
    requestedByColumn,
    lastModifiedColumn,
    approvalTypeColumn,
  ];
}
